import type { ProjectDetail, ProjectMember } from '@fos/shared/contracts/projects';
import { PROJECT_MEMBER_ROLE_LABELS } from '@fos/shared/labels';
import { Crown, HandCoins, MoreHorizontal, UserMinus, Users } from 'lucide-react';
import { useMemo, useState } from 'react';
import { errorMessage } from '../../../lib/api';
import { useActiveSession } from '../../../lib/auth';
import { formatRate } from '../../../lib/format';
import { trpc } from '../../../lib/trpc';
import { useAccess } from '../../../lib/useAccess';
import { Avatar } from '../../../ui/Avatar';
import { Button } from '../../../ui/Button';
import { Combobox, type ComboboxOption } from '../../../ui/Combobox';
import { Dialog } from '../../../ui/Dialog';
import { Field } from '../../../ui/Field';
import { Input } from '../../../ui/Input';
import { Menu, MenuContent, MenuItem, MenuTrigger } from '../../../ui/Menu';
import { SectionHeader } from '../../../ui/PageHeader';
import { Section } from '../../../ui/Section';
import { EmptyState } from '../../../ui/States';
import { toast } from '../../../ui/Toast';
import { TOUCH_ICON, TOUCH_TEXT } from '../../mobile';

/**
 * Zespół projektu (szczegóły projektu, Przegląd): prowadzący i członkowie. Dodanie osoby otwiera jej projekt
 * w zakresie „Tylko przypisani” (od następnego żądania). Zmiany tylko z `can.manageMembers` (backend sprawdza).
 * Stawka partnera (DECISIONS 2026-10-08 [P]): owner / admin / manager widzą i ustawiają ją przy każdej osobie
 * (`can.partnerRates`; manager nie zmienia własnej), osoba widzi swoją.
 */

/** „Stawka partnera: 120 zł/h” albo „domyślna 80 zł/h”; null = brak stawki (albo pole niewidoczne dla osoby). */
function partnerRateText(m: ProjectMember): string | null {
  if (m.partnerRate) return formatRate(m.partnerRate, 'PLN');
  if (m.defaultPartnerRate) return `domyślna ${formatRate(m.defaultPartnerRate, 'PLN')}`;
  return null;
}
export function ProjectMembers({ project }: { project: ProjectDetail }) {
  const session = useActiveSession();
  const utils = trpc.useUtils();
  const canManage = project.can.manageMembers;
  const canRates = project.can.partnerRates && !project.archivedAt;
  const access = useAccess();
  const [adding, setAdding] = useState(false);
  const [rateFor, setRateFor] = useState<ProjectMember | null>(null);

  const team = trpc.team.listMembers.useQuery(undefined, { enabled: adding });
  const add = trpc.projects.members.add.useMutation();
  const remove = trpc.projects.members.remove.useMutation();
  const update = trpc.projects.update.useMutation();

  const refresh = () =>
    Promise.all([utils.projects.get.invalidate({ id: project.id }), utils.projects.list.invalidate()]);

  const candidates = useMemo<ComboboxOption[]>(() => {
    const inProject = new Set(project.members.map((m) => m.userId));
    return (team.data ?? [])
      .filter((m) => m.status === 'active' && !inProject.has(m.userId))
      .map((m) => ({
        value: m.userId,
        label: m.name,
        description: m.email,
        leading: <Avatar initials={m.initials} color={m.avatarColor} />,
      }));
  }, [team.data, project.members]);

  const onAdd = async (userId: string) => {
    try {
      await add.mutateAsync({ projectId: project.id, userId, role: 'member' });
      setAdding(false);
      await refresh();
      toast.success('Dodano osobę do projektu');
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  const onRemove = async (userId: string, name: string) => {
    try {
      await remove.mutateAsync({ projectId: project.id, userId });
      await refresh();
      toast.success(`${name} nie jest już w projekcie`);
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  const onLead = async (userId: string) => {
    try {
      await update.mutateAsync({ id: project.id, version: project.version, leadUserId: userId });
      await refresh();
      toast.success('Zmieniono prowadzącego');
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  return (
    <Section aria-labelledby="project-members-title">
      <SectionHeader
        tone="strong"
        title={<span id="project-members-title">Zespół</span>}
        action={
          canManage && !adding ? (
            <button
              type="button"
              onClick={() => setAdding(true)}
              className={`rounded-xs text-body-s font-medium text-accent hover:underline focus-visible:outline-2 focus-visible:outline-accent ${TOUCH_TEXT}`}
            >
              Dodaj
            </button>
          ) : undefined
        }
      />
      {adding && (
        <div className="flex items-center gap-2 border-b border-line py-2">
          <Combobox
            aria-label="Osoba do dodania"
            value={null}
            onValueChange={(v) => void onAdd(v)}
            options={candidates}
            placeholder={team.isPending ? 'Wczytywanie zespołu…' : 'Wybierz osobę z zespołu'}
            searchPlaceholder="Szukaj osoby…"
            emptyText={team.isError ? 'Nie udało się wczytać zespołu' : 'Wszyscy są już w projekcie'}
            disabled={add.isPending}
            size="s"
          />
          <Button variant="ghost" size="s" onClick={() => setAdding(false)}>
            Anuluj
          </Button>
        </div>
      )}
      {project.members.length === 0 ? (
        <EmptyState
          compact
          icon={Users}
          title="Nikt nie jest przypisany do projektu"
          description="Osoby z zakresem „Tylko przypisani” zobaczą projekt dopiero po dodaniu."
        />
      ) : (
        <ul aria-label="Członkowie projektu">
          {project.members.map((m) => (
            <li key={m.id} className="flex min-h-12 items-center gap-3 border-b border-line py-2">
              <Avatar initials={m.initials} color={m.avatarColor} />
              <div className="min-w-0 flex-1">
                <p className="truncate text-body-s font-medium text-fg">
                  {m.name}
                  {m.userId === session.user.id && (
                    <span className="ml-2 text-caption font-normal text-fg-muted">Ty</span>
                  )}
                </p>
                {m.disabled && <p className="text-caption text-fg-muted">dostęp wyłączony</p>}
                {partnerRateText(m) && (canRates || m.userId === session.user.id) && (
                  <p className="text-caption text-fg-muted">
                    {m.userId === session.user.id && !canRates ? 'Moja stawka' : 'Stawka partnera'}:{' '}
                    {partnerRateText(m)}
                  </p>
                )}
              </div>
              <span className="shrink-0 text-caption text-fg-secondary">{PROJECT_MEMBER_ROLE_LABELS[m.role]}</span>
              {(canManage || canRates) && (
                <Menu>
                  <MenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="s"
                      icon={MoreHorizontal}
                      aria-label={`Akcje: ${m.name}`}
                      className={TOUCH_ICON}
                    />
                  </MenuTrigger>
                  <MenuContent align="end">
                    {canManage && m.role !== 'lead' && !m.disabled && (
                      <MenuItem icon={Crown} onSelect={() => void onLead(m.userId)}>
                        Ustaw jako prowadzącego
                      </MenuItem>
                    )}
                    {canRates && !(access.role === 'manager' && m.userId === session.user.id) && (
                      <MenuItem icon={HandCoins} onSelect={() => setTimeout(() => setRateFor(m), 0)}>
                        Stawka partnera…
                      </MenuItem>
                    )}
                    {canManage && (
                      <MenuItem icon={UserMinus} tone="danger" onSelect={() => void onRemove(m.userId, m.name)}>
                        Usuń z projektu
                      </MenuItem>
                    )}
                  </MenuContent>
                </Menu>
              )}
            </li>
          ))}
        </ul>
      )}
      <PartnerRateDialog
        projectId={project.id}
        member={rateFor}
        onClose={() => setRateFor(null)}
        onSaved={() => void refresh()}
      />
    </Section>
  );
}

function PartnerRateDialog({
  projectId,
  member,
  onClose,
  onSaved,
}: {
  projectId: string;
  member: ProjectMember | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [text, setText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [lastId, setLastId] = useState<string | null>(null);
  const utils = trpc.useUtils();
  const save = trpc.projects.members.setPartnerRate.useMutation();
  if (member && member.id !== lastId) {
    setLastId(member.id);
    setText(member.partnerRate ? member.partnerRate.replace(/\.00$/, '') : '');
    setError(null);
  }
  const submit = async () => {
    if (!member) return;
    const value = text.trim().replace(',', '.').replace(/\s/g, '');
    if (value && !/^\d{1,8}(\.\d{1,2})?$/.test(value)) {
      setError('Wpisz stawkę, np. 120 albo 120,50');
      return;
    }
    try {
      await save.mutateAsync({ projectId, userId: member.userId, partnerRate: value || null });
      await utils.time.invalidate();
      toast.success(`Zapisano stawkę partnera: ${member.name}`);
      onSaved();
      onClose();
    } catch (err) {
      setError(errorMessage(err, 'Nie udało się zapisać stawki.'));
    }
  };
  return (
    <Dialog
      open={member !== null}
      onOpenChange={(o) => !o && onClose()}
      width="s"
      title={member ? `Stawka partnera: ${member.name}` : 'Stawka partnera'}
      description="Ile płacisz tej osobie za godzinę w tym projekcie (koszt, nie cena dla klienta). Obejmuje też jej nierozliczone wpisy."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Anuluj
          </Button>
          <Button variant="primary" loading={save.isPending} onClick={() => void submit()}>
            Zapisz
          </Button>
        </>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <Field
          label="Stawka partnera (zł/h)"
          error={error}
          hint={
            member?.defaultPartnerRate
              ? `Puste = domyślna z Zespołu (${formatRate(member.defaultPartnerRate, 'PLN')})`
              : 'Puste = bez stawki partnera'
          }
        >
          <Input mono inputMode="decimal" value={text} onChange={(e) => setText(e.target.value)} placeholder="0" />
        </Field>
      </form>
    </Dialog>
  );
}
